import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthOptimizationRun, GrowthOptimizationService, GrowthOptimizer } from './growth-optimization.service';

@Component({
  standalone: true,
  selector: 'ain-growth-optimizer-runs',
  imports: [FormsModule, RouterLink, AinPageComponent, AinStateComponent, InfoHintComponent, TableModule, ButtonModule, SelectModule, AinDatePipe],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.optimization.runs.title')" [description]="t('growth.optimization.runs.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.optimization.runs.help.title'"
        [descriptionKey]="'growth.optimization.runs.help.description'"
        [bulletKeys]="['growth.optimization.runs.help.bullets.guardrails', 'growth.optimization.runs.help.bullets.workflow']" />

      <section class="card">
        <div class="toolbar">
          <p-select [options]="statusOptions" [(ngModel)]="statusFilter" optionLabel="label" optionValue="value" appendTo="body" [placeholder]="t('growth.optimization.runs.filters.status')" />
          <p-select [options]="optimizerOptions()" [(ngModel)]="optimizerFilter" optionLabel="label" optionValue="value" appendTo="body" [placeholder]="t('growth.optimization.runs.filters.optimizer')" />
          <button pButton type="button" [label]="t('common.refresh')" (click)="load()"></button>
        </div>
      </section>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (runs().length === 0) {
        <ain-state type="empty" [title]="t('growth.optimization.runs.empty')" />
      } @else {
        <section class="card">
          <p-table [value]="runs()" [rows]="10" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.optimization.runs.columns.optimizer') }}</th>
                <th>{{ t('growth.optimization.runs.columns.status') }}</th>
                <th>{{ t('growth.optimization.runs.columns.startedAt') }}</th>
                <th>{{ t('growth.optimization.runs.columns.completedAt') }}</th>
                <th>{{ t('growth.optimization.runs.columns.workflow') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.optimizerId }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.startedAtUtc | ainDate:'datetime' }}</td>
                <td>{{ row.completedAtUtc ? (row.completedAtUtc | ainDate:'datetime') : '-' }}</td>
                <td>{{ row.workflowProcessInstanceId ?? '-' }}</td>
                <td>
                  <a [routerLink]="['/growth/optimizer/runs', row.id]">{{ t('common.details') }}</a>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
  `]
})
export class GrowthOptimizerRunsComponent {
  private readonly service = inject(GrowthOptimizationService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly runs = signal<GrowthOptimizationRun[]>([]);
  readonly optimizers = signal<GrowthOptimizer[]>([]);

  statusFilter: string | null = null;
  optimizerFilter: string | null = null;

  readonly statusOptions = [
    { label: 'Queued', value: 'Queued' },
    { label: 'Analyzing', value: 'Analyzing' },
    { label: 'Simulating', value: 'Simulating' },
    { label: 'PendingApproval', value: 'PendingApproval' },
    { label: 'Approved', value: 'Approved' },
    { label: 'Executed', value: 'Executed' },
    { label: 'Blocked', value: 'Blocked' },
    { label: 'Failed', value: 'Failed' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  constructor() {
    this.loadOptimizers();
    this.load();
  }

  t(key: string): string { return this.i18n.translate(key); }

  optimizerOptions() {
    return this.optimizers().map(item => ({ label: item.name, value: item.id }));
  }

  loadOptimizers(): void {
    this.service.listOptimizers().subscribe({
      next: rows => this.optimizers.set(rows),
      error: () => this.optimizers.set([])
    });
  }

  load(): void {
    this.loading.set(true);
    this.service.listRuns({
      status: this.statusFilter ?? undefined,
      optimizerId: this.optimizerFilter ?? undefined
    }).subscribe({
      next: rows => {
        this.runs.set(rows);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }
}
